import React, { useEffect, useRef } from 'react';
import { Map } from 'google-maps-react-markers';
import { isInSoftBlockMode } from 'constants/geoBlocking';
import { COACHES_LANDING_PAGE } from 'constants/pages';
import { SportsEnum } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import { useInView } from 'hooks/useInView';
import { useModal } from 'hooks/useModal';
import FilterIcon from 'svg/FilterIcon';
import Plus from 'svg/Plus';
import SafeAreaPage from 'layouts/SafeAreaPage';
import { LessonPageModeEnum } from 'screens/ExploreLessons/enums';
import { ExploreLessonsComponentProps } from 'screens/ExploreLessons/props';
import Breadcrumbs from 'components/Breadcrumbs';
import Button, { ButtonLink } from 'components/Button';
import Footer from 'components/Footer';
import DistanceDropdown from 'components/DiscoverPage/DistanceDropdown';
import FilterModal from 'components/DiscoverPage/FilterModal';
import { ExploreAllMarketPlacesTypesEnum } from 'components/DiscoverPage/props';
import MapToggleBar from 'components/Explore/MapToggleBar';
import GoogleMap from 'components/GoogleMap';
import TopNav from 'components/nav/TopNav';
import TabSwitcher from 'components/TabSwitcher';
import classNames from 'styles/utils/classNames';
import SortDropdown from './SortDropdown';
import { AVAILABILITY_OPTIONS } from './LessonFiltersModal/LessonFilters';
import useLesson from './useLesson';

const ExploreLessons: React.FC<ExploreLessonsComponentProps> = ({
  pageMode,
  initialSport,
  cityData,
  coaches,
  breadcrumbs,
}) => {
  const cityCoordinates = cityData
    ? {
        latitude: cityData.latitude,
        longitude: cityData.longitude,
      }
    : undefined;

  const serverCoachData = coaches
    ? {
        coaches,
      }
    : undefined;

  const {
    LessonList,
    lessonMapProps,
    distance,
    setDistance,
    LessonFilters,
    setSortBy,
    sortBy,
    handleClearAllFilters,
    lessonsLength,
    onAddressChange,
    isOpenMap,
    handleToggleMap,
    availability,
    setAvailability,
  } = useLesson({
    pageMode,
    initialSport,
    initialCityCoordinates: cityCoordinates,
    serverCoachData,
  });

  const { closeModal, openModal, isOpen: isModalOpen } = useModal();
  const discoverContainerRef = useRef<HTMLDivElement | null>(null);

  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;

  const isDiscoverSectionInView = useInView(discoverContainerRef);

  const handleGoogleApiLoaded = (map: Map) => {
    map.setOptions({
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT,
      },
    });
  };

  useEffect(() => {
    if (discoverContainerRef.current && isOpenMap)
      discoverContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
  }, [isOpenMap]);

  return (
    <SafeAreaPage
      isHideSidebar
      shouldHideTabBar={pageMode !== LessonPageModeEnum.EXPLORE || isOpenMap}
    >
      {pageMode === LessonPageModeEnum.EXPLORE && (
        <TopNav
          shouldHideNavigation={false}
          shouldShowMobile
          shouldLinkToAuthPage={false}
          shouldShowAdditionalLinks={false}
          topNavSearch={{
            isEnabled: true,
            isSecondRowOverlay: isOpenMap,
            isSecondRowScrollable: !isOpenMap,
            defaultExploreType: ExploreAllMarketPlacesTypesEnum.Lessons,
            defaultSport: initialSport,
            onAddressChange,
            instant: true,
          }}
        />
      )}
      <div
        ref={discoverContainerRef}
        className={classNames(
          'relative grid w-full grid-cols-[minmax(0,1fr)_45%]',
          hasBreadcrumbs ? 'grid-rows-[auto_auto_1fr]' : 'grid-rows-[auto_1fr]',
          isOpenMap
            ? hasBreadcrumbs
              ? "[grid-template-areas:'header_header''extra-header_map''list_map'] max-lg:h-[calc(100vh-theme(spacing.topnav-mobile))] max-lg:grid-rows-[1fr] max-lg:[grid-template-areas:'map_map']"
              : "[grid-template-areas:'header_header''list_map'] max-lg:h-[calc(100vh-theme(spacing.topnav-mobile))] max-lg:grid-rows-[1fr] max-lg:[grid-template-areas:'map_map']"
            : hasBreadcrumbs
            ? "h-full max-lg:[grid-template-areas:'header_header''extra-header_extra-header''list_list'] lg:min-h-[calc(100vh-theme(spacing.topnav-search))] lg:[grid-template-areas:'header_header''extra-header_map''list_map']"
            : "h-full max-lg:[grid-template-areas:'header_header''list_list'] lg:min-h-[calc(100vh-theme(spacing.topnav-search))] lg:[grid-template-areas:'header_header''list_map']",
        )}
      >
        <div
          className={classNames(
            'sticky z-10 flex flex-col gap-4 border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4  dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary ',
            '[grid-area:header]',
            pageMode === LessonPageModeEnum.EXPLORE && 'top-topnav-mobile lg:top-topnav-search',
            isOpenMap && 'max-lg:hidden',
            !hasBreadcrumbs && 'sm:border-b',
          )}
        >
          <div className="flex gap-6 max-lg:flex-col max-lg:gap-ds-md lg:items-center">
            <div className="flex items-center gap-3 max-lg:justify-between">
              <h3 className="typography-product-heading-desktop max-lg:typography-product-heading-mobile shrink-0 whitespace-nowrap">
                {pluralize({ count: lessonsLength, singular: 'Coach', plural: 'Coaches' })} in this
                area
              </h3>
            </div>

            <div className="hidden grow-[3] items-center gap-3 sm:flex">
              {pageMode === LessonPageModeEnum.EXPLORE && (
                // Fixed-width wrapper: TabSwitcher is `w-full` internally, and classNames
                // does not merge Tailwind, so sizing has to come from the parent. Shown only
                // from `lg` up — below that these tabs live in the filters modal (which hides
                // them with `lg:hidden`), so the two never overlap or leave a gap.
                <div className="w-[16rem] shrink-0 max-lg:hidden">
                  <TabSwitcher
                    options={AVAILABILITY_OPTIONS}
                    selectedValue={availability}
                    onValueChange={setAvailability}
                    keepIconColor
                  />
                </div>
              )}
              <DistanceDropdown
                distance={distance}
                handleDistanceChange={(distance) => setDistance(distance)}
                activeTabValue={ExploreAllMarketPlacesTypesEnum.Lessons}
              />
            </div>

            <div className="flex items-center gap-3 max-lg:hidden">
              <SortDropdown setSortBy={setSortBy} sortBy={sortBy} />
              <Button
                onClick={() => openModal()}
                variant="inverted"
                className="w-fit shrink-0 whitespace-nowrap"
                size="md"
                iconLeft={
                  <FilterIcon className="h-4 w-4 text-color-bg-lightmode-invert dark:text-color-bg-darkmode-invert" />
                }
              >
                More filters
              </Button>
            </div>
          </div>
        </div>

        {hasBreadcrumbs && (
          <div
            className={classNames(
              'p-4 pt-0 [grid-area:extra-header]',
              isOpenMap && 'max-lg:hidden',
            )}
          >
            <Breadcrumbs breadcrumbs={breadcrumbs || []} />
          </div>
        )}

        {/* List section */}
        <div
          className={classNames(
            'flex min-w-0 flex-col overflow-auto',
            '[grid-area:list]',
            isOpenMap ? 'hidden lg:flex' : 'flex',
          )}
        >
          {pageMode === LessonPageModeEnum.EXPLORE && (
            <div className="px-4 pb-ds-lg pt-ds-xl sm:px-8">
              <ButtonLink
                href={COACHES_LANDING_PAGE}
                variant="secondary"
                size="md"
                className="w-fit shrink-0 whitespace-nowrap"
                iconLeft={<Plus className="h-4 w-4" />}
              >
                Add your coach account
              </ButtonLink>
            </div>
          )}
          {LessonList}
        </div>

        {/* Map section */}
        <div
          className={classNames(
            'sticky self-start [grid-area:map]',
            isOpenMap
              ? 'block max-lg:h-full lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]'
              : 'max-lg:hidden lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]',
          )}
        >
          <GoogleMap
            {...lessonMapProps}
            onGoogleApiLoaded={handleGoogleApiLoaded}
            softBlockPlaceholder={isInSoftBlockMode()}
          />
        </div>
      </div>

      <MapToggleBar
        isOpenMap={isOpenMap}
        onToggleMap={handleToggleMap}
        isInView={isDiscoverSectionInView}
        onOpenFilters={() => openModal()}
      />

      <FilterModal
        title={'Filters'}
        isOpen={isModalOpen}
        closeModal={closeModal}
        clearAll={handleClearAllFilters}
        closeButtonText={`Show ${lessonsLength} Coaches`}
      >
        {LessonFilters}
      </FilterModal>

      {pageMode === LessonPageModeEnum.EXPLORE && <Footer />}
    </SafeAreaPage>
  );
};

export default ExploreLessons;
